import {Divider, Modal, Space, Table} from 'antd';
import {Settlement} from 'api/generated';
import {AnchorLink, Hint, PropertyList} from 'components';
import {PayoutStatusTag} from 'components/PayoutStatusTag';
import {useAccessLevel} from 'hooks/useAccessLevel';
import {formatDateRange, formatIBAN, formatIntAmount} from 'lib/formaters';
import moment from 'moment';
import React, {useState} from 'react';
import {acquirers, paymentMethodsEnum} from 'types';

type Props = {
  payout: Settlement;
};

export const PayoutActions: React.FC<Props> = ({payout}) => {
  const [isOpen, setOpen] = useState(false);
  const {isAdmin} = useAccessLevel();

  const payoutPreviewData = [
    {
      name: 'Gross volume',
      amount: formatIntAmount(payout.grossVolume)
    },
    {
      name: (
        <Hint
          title="Acquiring fees"
          placement="topLeft"
          message="Fees charged for each transaction by all financial providers involved including the issuing bank and card schemes"></Hint>
      ),
      amount: formatIntAmount(payout.acquirerFees)
    },
    ...(payout.releasedAmount
      ? [
          {
            name: (
              <Hint
                title="Released amount"
                placement="topLeft"
                message="This amount was reserved in the previous payout"></Hint>
            ),
            amount: formatIntAmount(payout.releasedAmount)
          }
        ]
      : []),
    ...(payout.reservedAmount
      ? [
          {
            name: (
              <Hint
                title="Reserved amount"
                placement="topLeft"
                message="This amount will be released in the next payout"></Hint>
            ),
            amount: formatIntAmount(payout.reservedAmount)
          }
        ]
      : []),
    {
      name: <strong>Total paid out</strong>,
      amount: <strong>{formatIntAmount(payout.netVolume)}</strong>
    }
  ];

  return (
    <>
      <Space split={<Divider type="vertical" />}>
        <AnchorLink onClick={() => setOpen(true)}>preview</AnchorLink>
      </Space>
      <Modal
        bodyStyle={{padding: 0}}
        width={650}
        title={<h3 style={{marginBottom: 0}}>Transfer {formatIntAmount(payout.netVolume)}</h3>}
        footer={false}
        open={isOpen}
        onCancel={() => setOpen(false)}>
        {
          <Table
            pagination={false}
            style={{marginTop: -1}}
            dataSource={payoutPreviewData}
            bordered
            title={() => (
              <PropertyList
                omitEmpty
                properties={[
                  {
                    label: 'Status',
                    value: <PayoutStatusTag payout={payout} />
                  },
                  {
                    label: (
                      <Hint
                        placement="topLeft"
                        title="Period"
                        message="The date of the first and last payment included in the payout"></Hint>
                    ),
                    value: formatDateRange(payout.periodStartAt, payout.periodEndAt)
                  },
                  {
                    label: (
                      <Hint
                        placement="topLeft"
                        title="Completion date"
                        message="You can expect the bank transfer for this payout after this date."
                      />
                    ),
                    value: moment.unix(payout.scheduledAt).format('LL')
                  },
                  {
                    label: 'IBAN',
                    value: formatIBAN(payout.iban, true)
                  },
                  ...(payout.storeId
                    ? [
                        {
                          label: 'Store',
                          value: payout.legalName
                        }
                      ]
                    : []),
                  {
                    label: 'Payment method',
                    value: paymentMethodsEnum[payout.payMethod || 'card']
                  },
                  {
                    label: 'Acquirer',
                    value: isAdmin ? acquirers[payout.acquirer] : null
                  }
                ]}
              />
            )}
            columns={[
              {
                title: 'Name',
                dataIndex: 'name',
                key: 'name'
              },
              {
                title: 'Amount',
                dataIndex: 'amount',
                key: 'amount',
                align: 'right'
              }
            ]}
          />
        }
      </Modal>
    </>
  );
};
